import AppPaths from '../../AppPaths';
import type { FiCountry } from '../../contexts/LocaleContext';
import * as types from '../../types';
import { isUltraProduct } from '../../types/Product';
import { Species } from '../../types/gql-op-types';
import type { ActivePromotionSummary } from '../checkoutEmailCaptured';
import { isBundleCartItem, isGiftCardCartItem, promoUnitPriceInCents } from '../cart';
import {
  checkoutImageUrlForProduct,
  checkoutImageUrlForProductWithVariant,
  getProductsAndVariantsBySku,
} from '../product';
import { centsToDollarsNumber, getLocalizedPricing } from '../util/money';
import { hardwareFirstEventProperties } from './hardwareFirstEvents';

/**
 * The whole bag as a Customer.io event payload, for the cart-abandonment campaign. Keys match web's
 * `Checkout Email Captured` event (snake_case, flat `product_names` / `device_types` mirrors of the
 * `products` list) so the emails can read either event the same way. A field the client cannot know
 * is left undefined, which drops it from the JSON; nothing is sent as an empty string.
 */

export type PetType = 'dog' | 'cat';

export interface CartSnapshotInput {
  cart: types.StoreCart;
  products: types.IProduct[];
  /** the server's preview for this cart; undefined when it failed, leaving only catalog figures */
  cartPricing?: types.CartPricing;
  country: FiCountry;
  currency: types.FiCurrency;
  /** origin the email links back to, e.g. https://shop.tryfi.com */
  shopUrl: string;
  activePromotion: ActivePromotionSummary;
  /** when the event is sent; decides which of an extended promotion's deadlines is in force */
  now: Date;
  /** the cart item just added, which the legacy `product_name` / `product_color` describe */
  addedCartItemId?: string;
  /** the species the listing was shopped for, when it says; applied to the added cart item only */
  addedPetType?: PetType;
}

export interface CartSnapshotProduct {
  sku: string;
  name: string;
  device_type: string;
  quantity: number;
  size?: string;
  color?: string;
  unit_price?: number;
  /** the unit price after every saving (promo and multi-tracker), as on Checkout Email Captured */
  promo_price?: number;
  image_url: string;
  pet_type?: PetType;
}

export interface CartSnapshotProperties {
  product_name?: string;
  product_color?: string;
  cart_url: string;
  shop_url: string;
  country: FiCountry;
  currency: types.FiCurrency;
  products: CartSnapshotProduct[];
  item_count: number;
  product_count: number;
  product_names: string;
  device_types: string;
  coupon_code?: string;
  promo_percent_off?: number;
  promo_ends_at?: string;
  promo_extended_ends_at?: string;
  cart_subtotal?: number;
  cart_total?: number;
  multi_tracker_discount?: number;
}

const roundToCents = (dollars: number) => Math.round(dollars * 100) / 100;

const sumDollars = (amounts: number[]) => roundToCents(amounts.reduce((sum, amount) => sum + amount, 0));

export function petTypeForSpecies(species: Species | undefined): PetType | undefined {
  return species === Species.CAT ? 'cat' : species === Species.DOG ? 'dog' : undefined;
}

/** The bundle's species pick when the shopper made one, else what the line or listing implies. */
function petType(
  cartItem: types.StoreCartItem,
  product: types.IProduct,
  input: CartSnapshotInput,
): PetType | undefined {
  if (isBundleCartItem(cartItem) && cartItem.species) {
    return petTypeForSpecies(cartItem.species);
  }
  // a single-line item does not keep the species it was shopped for, so only the item just added has one
  if (cartItem.cartItemId === input.addedCartItemId && input.addedPetType) {
    return input.addedPetType;
  }
  // the series 3+ collar kit is sold for dogs only; the mini and ultra are sold for either
  return product.category === types.ProductCategory.COLLAR_KIT ? 'dog' : undefined;
}

/**
 * Per-unit prices for one line. A hardware-first tracker is repriced by the server, which quotes its list price and
 * its price after both savings; anything else carries its catalog price, less the coupon saving Recurly gives that
 * line. The catalog prices the series 3+ and mini at 0 (their price lives on the hardware-first preview or in the
 * bundled membership), and a 0 list price is left out rather than sent.
 */
function linePrices(
  cartItem: types.StoreCartItem,
  catalogPriceInCents: number,
  hardwareDiscount: types.HardwareDiscountPreview | undefined,
  cartPricing: types.CartPricing | undefined,
) {
  if (hardwareDiscount) {
    return {
      unit_price: centsToDollarsNumber(hardwareDiscount.listPriceInCents),
      promo_price: centsToDollarsNumber(hardwareDiscount.discountedPriceInCents),
    };
  }
  if (catalogPriceInCents <= 0) {
    return {};
  }
  const unitPrice = centsToDollarsNumber(catalogPriceInCents);
  const promoPriceInCents = cartPricing && promoUnitPriceInCents(cartItem, catalogPriceInCents, cartPricing);
  return {
    unit_price: unitPrice,
    promo_price: promoPriceInCents === undefined ? undefined : centsToDollarsNumber(promoPriceInCents),
  };
}

/**
 * An ultra bought with its own membership is priced from `standalonePricingInCents`, as the cart shows it;
 * one added to a membership the shopper already has, or any other line, from its catalog price.
 */
function catalogPricing(cartItem: types.StoreCartItem, product: types.IProduct, sku: string) {
  return isBundleCartItem(cartItem) && sku === cartItem.lineItem.sku && isUltraProduct(product)
    ? product.standalonePricingInCents
    : product.pricingInCents;
}

/**
 * The physical lines in the bag: trackers, bands and accessories, each bundle's hardware and add-ons
 * included. Memberships and gift cards are left out, as on Checkout Email Captured, since the email
 * rebuilds the bag from these SKUs and the abandoned-cart link only takes hardware.
 */
function cartProducts(input: CartSnapshotInput): CartSnapshotProduct[] {
  const { cart, cartPricing, currency } = input;
  const catalog = getProductsAndVariantsBySku(input.products);

  return Object.values(cart.cartItems)
    .filter((cartItem) => !isGiftCardCartItem(cartItem))
    .flatMap((cartItem) => {
      const addonSkus = isBundleCartItem(cartItem) ? (cartItem.addonLineItems ?? []).map((line) => line.sku) : [];
      const hardwareDiscount = isBundleCartItem(cartItem)
        ? undefined
        : cartPricing?.hardwareDiscounts?.[cartItem.cartItemId];

      return [cartItem.lineItem.sku, ...addonSkus].flatMap((sku) => {
        const entry = catalog.get(sku);
        if (!entry || entry.product.category === types.ProductCategory.SUBSCRIPTION) {
          return [];
        }
        const { product, variant } = entry;
        return [
          {
            sku,
            name: product.name,
            // lowercased category name, as web sends on Checkout Email Captured and Order Completed
            device_type: types.ProductCategory[product.category].toLowerCase(),
            quantity: cartItem.quantity,
            size: variant?.options.size || undefined,
            color: variant?.options.color || undefined,
            ...linePrices(
              cartItem,
              getLocalizedPricing(catalogPricing(cartItem, product, sku), currency),
              sku === cartItem.lineItem.sku ? hardwareDiscount : undefined,
              cartPricing,
            ),
            image_url: new URL(
              variant
                ? checkoutImageUrlForProductWithVariant(product.id, variant)
                : checkoutImageUrlForProduct(product.id),
              input.shopUrl,
            ).toString(),
            pet_type: petType(cartItem, product, input),
          },
        ];
      });
    });
}

/**
 * An extended promotion has two deadlines. Like the promo banner, `promo_ends_at` is the one in force when
 * the event is sent: the promotion's own end until the extension begins, the extended end after, so the
 * extension days are not revealed early. `promo_extended_ends_at` carries the extended end whenever there
 * is an extension, so an email sent later, during the extension, can show the deadline then in force.
 */
function promoDeadlines({ end, extendedFrom }: ActivePromotionSummary, now: Date) {
  const inForce = extendedFrom && now < extendedFrom ? extendedFrom : end;
  return {
    promo_ends_at: inForce?.toISOString(),
    promo_extended_ends_at: extendedFrom ? end?.toISOString() : undefined,
  };
}

/**
 * The saving for buying more than one tracker: hardware-first's per-tracker discount, which the server
 * already sums over the bag, or the control checkout's multi-dog membership credits.
 */
function multiTrackerDiscountInCents(cartPricing: types.CartPricing | undefined) {
  const householdCredits = Object.values(cartPricing?.cartItemCredits ?? {})
    .flat()
    .filter((credit) => credit.creditReason.startsWith('household_discount'))
    .map((credit) => credit.creditAmountInCents);
  return [cartPricing?.hardwareFirstMultiDeviceDiscountInCents ?? 0, ...householdCredits].reduce(
    (sum, amount) => sum + amount,
    0,
  );
}

export type LegacyAddedItemProperties = Pick<CartSnapshotProperties, 'product_name' | 'product_color'>;

/**
 * The added item's product id and band colour, the only fields the event carried before the snapshot. Sent in its
 * place when the snapshot cannot be built, so the campaign still starts.
 */
export function legacyAddedItemProperties(products: types.IProduct[], sku: string): LegacyAddedItemProperties {
  const added = getProductsAndVariantsBySku(products).get(sku);
  return { product_name: added?.product.id, product_color: added?.variant?.options.color || undefined };
}

/**
 * Undefined for a bag with no physical lines (e.g. only a gift card), which the email has nothing to show
 * for, and when the added cart item is not in this bag.
 */
export function buildCartSnapshot(input: CartSnapshotInput): CartSnapshotProperties | undefined {
  const { cart, cartPricing, activePromotion, addedCartItemId } = input;
  const addedItem = addedCartItemId ? cart.cartItems[addedCartItemId] : undefined;
  const products = cartProducts(input);
  if (products.length === 0 || (addedCartItemId && !addedItem)) {
    return undefined;
  }

  // a code the server rejected is dropped from the bag on the next render, so it is not reported
  const couponCode = cartPricing?.validationErrors?.couponCode ? undefined : cart.couponCode || undefined;
  const promoIsLive = !!couponCode && couponCode.toLowerCase() === activePromotion.couponCode?.toLowerCase();
  const hardwareFirst = hardwareFirstEventProperties(cartPricing, couponCode);
  const couponPercent =
    cartPricing?.couponDiscount?.type === 'percent' ? cartPricing.couponDiscount.percent : undefined;
  const multiTrackerInCents = multiTrackerDiscountInCents(cartPricing);

  return {
    // the legacy fields the emails fall back to
    ...(addedItem && legacyAddedItemProperties(input.products, addedItem.lineItem.sku)),
    cart_url: new URL(AppPaths.Bag, input.shopUrl).toString(),
    shop_url: input.shopUrl,
    country: input.country,
    currency: input.currency,
    products,
    item_count: products.reduce((sum, product) => sum + product.quantity, 0),
    product_count: products.length,
    product_names: products.map((product) => product.name).join(', '),
    device_types: [...new Set(products.map((product) => product.device_type))].join(', '),
    coupon_code: couponCode,
    promo_percent_off: hardwareFirst?.promo_percent || couponPercent,
    ...(promoIsLive && promoDeadlines(activePromotion, input.now)),
    // the list total of `products`; unknown as soon as one line's list price is
    cart_subtotal: products.every((product) => product.unit_price !== undefined)
      ? sumDollars(products.map((product) => (product.unit_price ?? 0) * product.quantity))
      : undefined,
    // before tax and shipping, which the in-app sheet's and logged-in bag's pricing include
    cart_total: cartPricing
      ? centsToDollarsNumber(
          cartPricing.totalInCents - (cartPricing.taxInCents ?? 0) - (cartPricing.shippingInCents ?? 0),
        )
      : undefined,
    multi_tracker_discount: multiTrackerInCents > 0 ? centsToDollarsNumber(multiTrackerInCents) : undefined,
  };
}
